import { Switch as BaseSwitch } from "@headlessui/react"
import cc from "classcat"
import { useController } from "react-hook-form"
import type { Control } from "react-hook-form"

interface SwitchProps {
  label: string
  value?: string | number
  required?: boolean
  name: string
  control: Control
  description?: string
}

export const Switch = (props: SwitchProps): JSX.Element => {
  const {
    field: { value, onChange, ref },
  } = useController({
    ...props,
  })

  const { label, description } = props

  return (
    <div className="block w-full sm:inline-flex">
      <div className="w-full">
        <BaseSwitch.Group>
          <div className="flex max-w-lg items-start space-x-4 font-bold text-zinc-600">
            <BaseSwitch
              checked={value}
              onChange={onChange}
              className={cc([
                value ? "bg-kkm-green" : "bg-zinc-200",
                "relative inline-flex h-10 w-20 items-center rounded-[0.6rem] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2",
              ])}
            >
              {/* button circle */}
              <span
                className={cc([
                  "pointer-events-none relative inline-block h-8 w-8 transform rounded-[0.4rem] bg-white shadow ring-0 transition duration-200 ease-in-out",
                  value ? "translate-x-11" : "translate-x-1",
                ])}
              >
                {/* cross */}
                <span
                  className={cc([
                    value
                      ? "opacity-0 duration-100 ease-out"
                      : "opacity-100 duration-200 ease-in",
                    "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                  ])}
                  aria-hidden="true"
                >
                  <svg
                    className="h-3 w-3 text-zinc-400"
                    fill="none"
                    viewBox="0 0 12 12"
                  >
                    <path
                      d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {/* checkmark */}
                <span
                  className={cc([
                    value
                      ? "opacity-100 duration-200 ease-in"
                      : "opacity-0 duration-100 ease-out",
                    "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                  ])}
                  aria-hidden="true"
                >
                  <svg
                    className="h-3 w-3 text-kkm-green"
                    fill="currentColor"
                    viewBox="0 0 12 12"
                  >
                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                  </svg>
                </span>
              </span>
            </BaseSwitch>
            <BaseSwitch.Label
              className="mr-4 flex-1 text-sm text-zinc-800"
              ref={ref}
            >
              {label}
              {description && (
                <>
                  <br />
                  <span className="leading-1 my-2 text-xs font-normal">
                    {description}
                  </span>
                </>
              )}
            </BaseSwitch.Label>
          </div>
        </BaseSwitch.Group>
      </div>
    </div>
  )
}
